/** Имена полей тела — filters и selectCheckBoxesData; в соседнем контракте проекта написание другое. */

import { AxiosPromise } from 'axios';

import '@/extensions';

import { MmRequestConfig } from '../../api/mmHttp';
import {
  createContinuousTask,
  createFreeTask,
  createKviTask,
  createTpcTask,
} from '../../api/tasks';
import { buildSelectCheckBoxesData, MmSelectionState, MM_PRODUCT_SELECTION_FIELDS } from '../../core/selection';
import { MmSkuKey } from '../../types/domain';
import {
  MmAnyCreateTaskRequest,
  MmCreateContinuousTaskRequest,
  MmCreateFreeTaskRequest,
  MmCreateKviTaskRequest,
  MmCreateTaskResponse,
  MmCreateTpcTaskRequest,
  MmSelectCheckBoxesData,
  MmTaskFilters,
} from '../../types/dto';
import { MmTaskType } from '../../types/enums';

/** «Все» в интерфейсе — один тег, в модели пустой массив; сентинел 0 срезает realValuesOrEmpty. */
export interface MmTaskCreateZone {
  formats: number[];
  regions: number[];
  clusters: number[];
}

/** Нуля здесь не бывает; пустой массив значит «уровень не участвует», а не «без фильтра» */
export interface MmTaskCreateLevels {
  groups: number[];
  categories: number[];
  subCategories: number[];
}

export interface MmTaskCreateFormState {
  type: MmTaskType;
  zone: MmTaskCreateZone;
  levels: MmTaskCreateLevels;
  comment: string;
  /** Опция прайс-чекера. Уходит в тело Свободного и KVI. */
  isPriceCheckerEnabled: boolean;
  selection: MmSelectionState<MmSkuKey>;
}

/** `realValuesOrEmpty()` — только к формату, региону и кластеру: на группах он молча меняет смысл */
export function prepareTaskFilters(
  state: MmTaskCreateFormState,
  loadedFormatIds?: MmTaskCreateFormatCatalog,
): MmTaskFilters {
  const raw = prepareFiltersFromParts(state.zone, state.levels);

  if (loadedFormatIds === undefined) {
    return raw;
  }

  return withCreateFormatExpansion(raw, loadedFormatIds);
}

/** Зависимости сужены намеренно: иначе каждый символ комментария перезапрашивал бы товары */
export function prepareFiltersFromParts(zone: MmTaskCreateZone, levels: MmTaskCreateLevels): MmTaskFilters {
  return {
    // `0` в селекте означает «Все» и в запрос не уходит
    formats: zone.formats.slice().realValuesOrEmpty(),
    regions: zone.regions.slice().realValuesOrEmpty(),
    clusters: zone.clusters.slice().realValuesOrEmpty(),
    // Пустой массив здесь значим сам по себе, значения «все» тут нет
    groups: levels.groups.slice(),
    categories: levels.categories.slice(),
    subCategories: levels.subCategories.slice(),
  };
}

/** null — справочник ещё грузится; в массиве только идентификаторы, без нуля «все» */
export type MmTaskCreateFormatCatalog = number[] | null;

/**
 * Пустой `formats` после схлопывания «Все» разворачивается в полный набор id,
 * но только когда справочник уже загружен.
 */
export function resolveCreateEffectiveFormats(
  formats: number[],
  loadedFormatIds: MmTaskCreateFormatCatalog,
): number[] {
  if (formats.length > 0) {
    return formats.slice();
  }

  if (loadedFormatIds === null || loadedFormatIds.length === 0) {
    return [];
  }

  return loadedFormatIds.slice();
}

export function withCreateFormatExpansion(
  filters: MmTaskFilters,
  loadedFormatIds: MmTaskCreateFormatCatalog,
): MmTaskFilters {
  return {
    formats: resolveCreateEffectiveFormats(filters.formats, loadedFormatIds),
    regions: filters.regions.slice(),
    clusters: filters.clusters.slice(),
    groups: filters.groups.slice(),
    categories: filters.categories.slice(),
    subCategories: filters.subCategories.slice(),
  };
}

function buildSelectionSection(state: MmTaskCreateFormState): MmSelectCheckBoxesData<MmSkuKey> {
  const raw = buildSelectCheckBoxesData<MmSkuKey>(state.selection, MM_PRODUCT_SELECTION_FIELDS);

  return {
    isCommonCheckboxSelected: raw.isCommonCheckboxSelected,
    includedSKUsInternalCodes: raw[MM_PRODUCT_SELECTION_FIELDS.included],
    excludedSKUsInternalCodes: raw[MM_PRODUCT_SELECTION_FIELDS.excluded],
  };
}

/** `filters` уходят всегда, даже когда бэкенд их игнорирует, — вырезать их нельзя */
function buildBase(
  state: MmTaskCreateFormState,
  tradingNetworkId: number,
  loadedFormatIds?: MmTaskCreateFormatCatalog,
): { filters: MmTaskFilters; comment: string; tradingNetworkId: number } {
  return {
    filters: prepareTaskFilters(state, loadedFormatIds),
    comment: state.comment,
    tradingNetworkId: tradingNetworkId,
  };
}

function buildFree(
  state: MmTaskCreateFormState,
  tradingNetworkId: number,
  loadedFormatIds?: MmTaskCreateFormatCatalog,
): MmCreateFreeTaskRequest {
  const base = buildBase(state, tradingNetworkId, loadedFormatIds);

  return {
    filters: base.filters,
    comment: base.comment,
    tradingNetworkId: base.tradingNetworkId,
    selectCheckBoxesData: buildSelectionSection(state),
    isPriceCheckerEnabled: state.isPriceCheckerEnabled,
  };
}

/** Фильтр «есть товар конкурента» добавляет бэкенд: при типе KVI список товаров приходит короче */
function buildKvi(
  state: MmTaskCreateFormState,
  tradingNetworkId: number,
  loadedFormatIds?: MmTaskCreateFormatCatalog,
): MmCreateKviTaskRequest {
  const base = buildBase(state, tradingNetworkId, loadedFormatIds);

  return {
    filters: base.filters,
    comment: base.comment,
    tradingNetworkId: base.tradingNetworkId,
    selectCheckBoxesData: buildSelectionSection(state),
    isPriceCheckerEnabled: state.isPriceCheckerEnabled,
  };
}

/** Ключа selectCheckBoxesData в теле нет вовсе; нижние уровни разворачивает сервер сам */
function buildTpc(
  state: MmTaskCreateFormState,
  tradingNetworkId: number,
  loadedFormatIds?: MmTaskCreateFormatCatalog,
): MmCreateTpcTaskRequest {
  const base = buildBase(state, tradingNetworkId, loadedFormatIds);

  return {
    filters: base.filters,
    comment: base.comment,
    tradingNetworkId: base.tradingNetworkId,
  };
}

function buildContinuous(
  state: MmTaskCreateFormState,
  tradingNetworkId: number,
  loadedFormatIds?: MmTaskCreateFormatCatalog,
): MmCreateContinuousTaskRequest {
  const base = buildBase(state, tradingNetworkId, loadedFormatIds);

  return {
    filters: base.filters,
    comment: base.comment,
    tradingNetworkId: base.tradingNetworkId,
  };
}

export function buildTaskCreatePayload(
  state: MmTaskCreateFormState,
  tradingNetworkId: number,
  loadedFormatIds?: MmTaskCreateFormatCatalog,
): MmAnyCreateTaskRequest {
  if (state.type === MmTaskType.KVI) {
    return buildKvi(state, tradingNetworkId, loadedFormatIds);
  }

  if (state.type === MmTaskType.FREE) {
    return buildFree(state, tradingNetworkId, loadedFormatIds);
  }

  if (state.type === MmTaskType.TPC) {
    return buildTpc(state, tradingNetworkId, loadedFormatIds);
  }

  return buildContinuous(state, tradingNetworkId, loadedFormatIds);
}

/** Повторной отправки нет: ключа идемпотентности в контракте нет, повтор создал бы вторую пачку. */
export function submitTaskCreate(
  state: MmTaskCreateFormState,
  tradingNetworkId: number,
  config?: MmRequestConfig,
  loadedFormatIds?: MmTaskCreateFormatCatalog,
): AxiosPromise<MmCreateTaskResponse> {
  if (state.type === MmTaskType.KVI) {
    return createKviTask(buildKvi(state, tradingNetworkId, loadedFormatIds), config);
  }

  if (state.type === MmTaskType.FREE) {
    return createFreeTask(buildFree(state, tradingNetworkId, loadedFormatIds), config);
  }

  if (state.type === MmTaskType.TPC) {
    return createTpcTask(buildTpc(state, tradingNetworkId, loadedFormatIds), config);
  }

  return createContinuousTask(buildContinuous(state, tradingNetworkId, loadedFormatIds), config);
}
